import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { classesAPI, SITE_COLORS } from '../../lib/api';
import { ArrowLeft, Users, GraduationCap } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const ClassesPage = ({ siteKey }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadClasses = async () => {
      try {
        const response = await classesAPI.getAll(siteKey);
        setClasses(response.data);
      } catch (error) {
        console.error('Error loading classes:', error);
      } finally {
        setLoading(false);
      }
    };
    loadClasses();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">
            {siteKey === 'ms' ? 'Naše trídy' : 'Naše skupiny'}
          </h1>
          <p className="text-[#64748B] mt-2">
            {siteKey === 'ms' ? 'Trídy rozdelené podle veku detí' : 'Rodinné prostredí pro vaše deti'}
          </p>
        </div>

        {classes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {classes.map((cls, index) => (
              <Link
                key={cls.id}
                to={`/${siteKey}/tridy/${cls.id}`}
                className="class-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`class-${index}`}
              >
                {cls.image_url && (
                  <div className="relative h-56">
                    <img src={cls.image_url} alt={cls.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span 
                        className="inline-block px-3 py-1 rounded-full bg-white/90 text-sm font-medium"
                        style={{ color: colors.primary }}
                      >
                        {cls.age_group}
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h2 className="font-bold text-2xl text-[#2D3748] mb-3">{cls.name}</h2>
                  <p className="text-[#64748B] mb-6 leading-relaxed">{cls.description}</p>
                  <div className="space-y-3 border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.primary}20` }}>
                        <GraduationCap size={16} style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <div className="text-[#64748B] text-xs">Třídní učitelka</div>
                        <div className="text-[#2D3748] font-medium">
                          {cls.teachers?.find(t => t.role === 'head_teacher')?.name || cls.teacher_name || '—'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-[#FECA57]/20 flex items-center justify-center">
                        <Users size={16} className="text-[#FF9F43]" />
                      </div>
                      <div>
                        <div className="text-[#64748B] text-xs">Kapacita</div>
                        <div className="text-[#2D3748] font-medium">{cls.capacity} detí</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="text-[#64748B]">Informace o trídách nejsou k dispozici</div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ClassDetailPage = ({ siteKey }) => {
  const { id } = useParams();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadClass = async () => {
      try {
        const response = await classesAPI.getOne(id);
        setClassData(response.data);
      } catch (error) {
        console.error('Error loading class:', error);
      } finally {
        setLoading(false);
      }
    };
    loadClass();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-[#2D3748]">Trída nenalezena</h1>
          <Link to={`/${siteKey}/tridy`} className="text-[#45AAF2] mt-4 inline-block">
            Zpet na trídy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title={classData?.name || 'Detail třídy'}
        description={`Detail ${siteKey === 'ms' ? 'třídy' : 'skupiny'} ${classData?.name || ''} - věková skupina ${classData?.age_group || ''}.`}
        image={classData?.image_url}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to={`/${siteKey}/tridy`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-6" data-testid="back-link">
          <ArrowLeft size={18} />
          Zpet na trídy
        </Link>

        <div className="bg-white rounded-3xl overflow-hidden border-3 border-[#F0F4F8] shadow-lg">
          {classData.image_url && (
            <img src={classData.image_url} alt={classData.name} className="w-full h-64 lg:h-80 object-cover" />
          )}
          <div className="p-8">
            <span 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
            >
              {classData.age_group}
            </span>
            <h1 className="text-3xl font-bold text-[#2D3748] mb-4" data-testid="class-title">{classData.name}</h1>
            <p className="text-[#64748B] text-lg leading-relaxed mb-8">{classData.description}</p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#F7F9FC] rounded-xl p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}20` }}>
                  <GraduationCap size={24} style={{ color: colors.primary }} />
                </div>
                <div>
                  <div className="text-sm text-[#64748B]">Třídní učitelka</div>
                  <div className="font-semibold text-[#2D3748]">
                    {classData.teachers?.find(t => t.role === 'head_teacher')?.name || classData.teacher_name || '—'}
                  </div>
                </div>
              </div>
              <div className="bg-[#F7F9FC] rounded-xl p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FECA57]/20 flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-[#FF9F43]" />
                </div>
                <div>
                  <div className="text-sm text-[#64748B]">Kapacita</div>
                  <div className="font-semibold text-[#2D3748]">{classData.capacity} dětí</div>
                </div>
              </div>
            </div>

            {classData.teachers?.length > 0 && (
              <div className="mt-6 bg-[#F7F9FC] rounded-xl p-5">
                <h3 className="font-semibold text-[#2D3748] mb-3 flex items-center gap-2">
                  <Users size={18} style={{ color: colors.primary }} /> Pedagogický tým
                </h3>
                <div className="space-y-2">
                  {classData.teachers.map((t, i) => {
                    const roleLabel = t.role === 'head_teacher' ? 'Třídní učitelka' : t.role === 'assistant' ? 'Asistentka pedagoga' : 'Učitelka';
                    return (
                      <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`class-team-${i}`}>
                        <span className="font-medium text-[#2D3748]">{t.name}</span>
                        <span className="text-sm px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
                          {roleLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassesPage;
